import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getDatabase, ref as dbRef, set, push, get } from 'firebase/database';
import { Attachment, ImageAttachment, FileAttachment } from '../types/attachments';
import { softDeleteFile } from '../api/fileDeletionApi';
import { recordActivity } from '../api/activityApi';
import { formatDateOnlyForStorage, formatDateForStorage } from './DateUtils';
import { getImageDimensions } from './ImageUploadUtils';
import { isImageFile } from './FileUtils';

export interface WorkOrderAttachmentUploadResult {
    success: boolean;
    attachment?: Attachment;
    error?: string;
}

/**
 * Find the database path for a work order (checks both active and archive)
 * Returns null if work order is not found in either location
 */
export const getWorkOrderDbPath = async (
    workOrderKey: string
): Promise<string | null> => {
    const db = getDatabase();

    // Check active first
    const activeSnapshot = await get(dbRef(db, `workOrders/active/${workOrderKey}`));
    if (activeSnapshot.exists()) {
        return `workOrders/active/${workOrderKey}`;
    }

    // Check archive (flat structure)
    const archiveSnapshot = await get(dbRef(db, `workOrders/archive/${workOrderKey}`));
    if (archiveSnapshot.exists()) {
        return `workOrders/archive/${workOrderKey}`;
    }

    return null;
};

/**
 * Upload a file attachment to a work order
 * Works for both active and archived work orders
 */
export const uploadWorkOrderAttachment = async (
    workOrderKey: string,
    file: File,
    uploadedBy: string,
    displayName?: string
): Promise<WorkOrderAttachmentUploadResult> => {
    try {
        const db = getDatabase();
        const storage = getStorage();

        // Find where the work order is stored
        const workOrderPath = await getWorkOrderDbPath(workOrderKey);
        if (!workOrderPath) {
            return {
                success: false,
                error: 'Ärendet hittades inte'
            };
        }

        // Generate unique attachment key using Firebase push
        const attachmentsRef = dbRef(db, `${workOrderPath}/attachments`);
        const newAttachmentRef = push(attachmentsRef);
        const attachmentKey = newAttachmentRef.key!;

        // Storage path for work order attachments
        const storagePath = `workOrders/${workOrderKey}/attachments/${attachmentKey}`;

        // Upload file to Storage
        const fileRef = storageRef(storage, storagePath);
        await uploadBytes(fileRef, file);

        // Get file extension
        const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';

        // Check if image and get dimensions if so
        const isImage = isImageFile(file);

        let attachment: Attachment;

        if (isImage) {
            // For images, use PhotoModel structure (can reuse existing image components)
            try {
                const dimensions = await getImageDimensions(file);

                const imageAttachment: ImageAttachment = {
                    key: attachmentKey,
                    author: uploadedBy,
                    date: formatDateForStorage(new Date()),
                    width: dimensions.width,
                    height: dimensions.height,
                    storagePath: storagePath,
                    fileSize: file.size,
                    attachmentType: 'image',
                    fileName: file.name,
                    mimeType: file.type,
                    ...(displayName && { displayName })
                };

                attachment = imageAttachment;
            } catch (error) {
                console.warn('Failed to get image dimensions:', error);
                // Fallback: create image attachment with default dimensions
                const imageAttachment: ImageAttachment = {
                    key: attachmentKey,
                    author: uploadedBy,
                    date: formatDateForStorage(new Date()),
                    width: 0,
                    height: 0,
                    storagePath: storagePath,
                    fileSize: file.size,
                    attachmentType: 'image',
                    fileName: file.name,
                    mimeType: file.type,
                    ...(displayName && { displayName })
                };
                attachment = imageAttachment;
            }
        } else {
            // For non-image files, use FileAttachment
            const fileAttachment: FileAttachment = {
                key: attachmentKey,
                fileName: file.name,
                fileType: fileExtension,
                mimeType: file.type,
                storagePath: storagePath,
                fileSize: file.size,
                uploadedBy: uploadedBy,
                uploadedAt: formatDateOnlyForStorage(new Date()),
                attachmentType: 'file',
                ...(displayName && { displayName })
            };

            attachment = fileAttachment;
        }

        // Save attachment metadata to RTDB
        await set(newAttachmentRef, attachment);
        recordActivity('document.uploaded', { targetKey: workOrderKey });

        return {
            success: true,
            attachment
        };
    } catch (error) {
        console.error('Error uploading work order attachment:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error occurred'
        };
    }
};

/**
 * Delete a work order attachment (soft delete — moves the metadata node to a
 * tombstone at deletedFiles/{key}; the Storage blob is left for a future
 * cleanup job)
 * Works for both active and archived work orders
 */
export const deleteWorkOrderAttachment = async (
    workOrderKey: string,
    attachmentKey: string
): Promise<{ success: boolean; error?: string }> => {
    try {
        // Find where the work order is stored (active vs archive)
        const workOrderPath = await getWorkOrderDbPath(workOrderKey);
        if (!workOrderPath) {
            return {
                success: false,
                error: 'Ärendet hittades inte'
            };
        }

        await softDeleteFile({
            originalPath: `${workOrderPath}/attachments/${attachmentKey}`,
            entityType: 'workOrderAttachment',
        });

        return {
            success: true
        };
    } catch (error) {
        console.error('Error deleting work order attachment:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error occurred'
        };
    }
};

/**
 * Get download URL for an attachment
 */
export const getWorkOrderAttachmentDownloadUrl = async (
    storagePath: string
): Promise<string> => {
    try {
        const storage = getStorage();
        const fileRef = storageRef(storage, storagePath);
        return await getDownloadURL(fileRef);
    } catch (error) {
        console.error('Error getting attachment download URL:', error);
        throw error;
    }
};
